import { useEffect, useMemo, useState } from "react";
import { addDays, eachDayOfInterval, isSameDay, startOfDay } from "date-fns";
import { AlertTriangle, CalendarRange, Check, Clock, Hand, Loader2, Plus, Sun } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import { cn } from "@/lib/utils";
import { createMaterialReserva, getApiErrorMessage, getMaterialReservas, updateMaterialReserva } from "@/lib/api";
import { buildReservaRango, formatReservaRango, horaRedondeada } from "@/lib/material";
import { MaterialEstadoBadge, MaterialFoto } from "@/components/material/MaterialShared";
import { es } from "date-fns/locale";

/**
 * config: { modo: "reservar" | "ahora" | "editar", itemIds?, desde?, hasta?, reserva? }
 * - reservar: elegir equipos + rango de días (día completo o con horas)
 * - ahora: el equipo se recoge ya; solo se elige hasta cuándo
 * - editar: cambia una reserva pendiente, o amplía la devolución si ya está en uso
 */
export default function ReservaDialog({ open, onOpenChange, config, items, usersByEmail, onDone }) {
  const hoy = startOfDay(new Date());
  const reserva = config?.reserva || null;
  const soloFin = config?.modo === "ahora" || (config?.modo === "editar" && reserva?.estado === "en_uso");

  const [selIds, setSelIds] = useState([]);
  const [rango, setRango] = useState(undefined);
  const [diaFin, setDiaFin] = useState(undefined);
  const [diaCompleto, setDiaCompleto] = useState(true);
  const [horaInicio, setHoraInicio] = useState("09:00");
  const [horaFin, setHoraFin] = useState("18:00");
  const [motivo, setMotivo] = useState("");
  const [mostrarMas, setMostrarMas] = useState(false);
  const [ocupadas, setOcupadas] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open || !config) return;
    const r = config.reserva;
    setSaving(false);
    setMostrarMas(false);
    if (r) {
      const ini = new Date(r.inicio);
      const fin = new Date(r.fin);
      const ultimo = r.dia_completo ? new Date(fin.getTime() - 1) : fin;
      setSelIds([r.item_id]);
      setRango({ from: startOfDay(ini), to: startOfDay(ultimo) });
      setDiaFin(startOfDay(ultimo));
      setDiaCompleto(!!r.dia_completo);
      setHoraInicio(r.dia_completo ? "09:00" : `${String(ini.getHours()).padStart(2, "0")}:${String(ini.getMinutes()).padStart(2, "0")}`);
      setHoraFin(r.dia_completo ? "18:00" : `${String(fin.getHours()).padStart(2, "0")}:${String(fin.getMinutes()).padStart(2, "0")}`);
      setMotivo(r.motivo || "");
    } else {
      const desde = config.desde ? startOfDay(config.desde) : hoy;
      const hasta = config.hasta ? startOfDay(config.hasta) : desde;
      setSelIds(config.itemIds || []);
      setRango({ from: desde, to: hasta });
      setDiaFin(hoy);
      setDiaCompleto(config.modo !== "ahora");
      setHoraInicio(isSameDay(desde, new Date()) ? horaRedondeada(new Date()) : "09:00");
      setHoraFin(config.modo === "ahora" ? horaRedondeada(new Date(), 2) : "18:00");
      setMotivo("");
    }
    getMaterialReservas({ desde: new Date().toISOString() })
      .then((d) => setOcupadas((d.reservas || []).filter((x) => ["reservada", "en_uso"].includes(x.estado))))
      .catch(() => setOcupadas([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, config]);

  const itemsById = useMemo(() => Object.fromEntries(items.map((i) => [i.id, i])), [items]);
  const seleccionados = selIds.map((id) => itemsById[id]).filter(Boolean);
  const otros = items.filter((i) => !selIds.includes(i.id) && i.estado !== "reparacion");

  const ocupadasSel = useMemo(
    () => ocupadas.filter((r) => selIds.includes(r.item_id) && r.id !== reserva?.id),
    [ocupadas, selIds, reserva]
  );

  const diasOcupados = useMemo(() => {
    const out = [];
    ocupadasSel.forEach((r) => {
      const ini = startOfDay(new Date(r.inicio));
      const fin = new Date(new Date(r.fin).getTime() - 1);
      if (fin < ini) return;
      eachDayOfInterval({ start: ini, end: fin }).forEach((d) => out.push(d));
    });
    return out;
  }, [ocupadasSel]);

  const periodo = useMemo(() => {
    if (soloFin) {
      if (!diaFin) return null;
      const inicio = reserva?.estado === "en_uso" ? reserva.inicio : new Date().toISOString();
      const { fin } = buildReservaRango({ desde: diaFin, hasta: diaFin, diaCompleto, horaInicio: "00:00", horaFin });
      return { inicio, fin };
    }
    if (!rango?.from) return null;
    return buildReservaRango({ desde: rango.from, hasta: rango.to || rango.from, diaCompleto, horaInicio, horaFin });
  }, [soloFin, diaFin, reserva, diaCompleto, horaFin, horaInicio, rango]);

  const errorPeriodo = !periodo
    ? "Elige las fechas"
    : new Date(periodo.fin) <= new Date(periodo.inicio)
      ? "La hora de fin debe ser posterior a la de inicio"
      : soloFin && new Date(periodo.fin) <= new Date()
        ? "La devolución debe ser en el futuro"
        : null;

  const conflictos = useMemo(() => {
    if (!periodo || errorPeriodo) return [];
    return ocupadasSel.filter((r) => r.inicio < toUtc(periodo.fin) && r.fin > toUtc(periodo.inicio));
  }, [ocupadasSel, periodo, errorPeriodo]);

  const toggleItem = (id) => setSelIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));

  const submit = async () => {
    if (errorPeriodo || !seleccionados.length) return;
    setSaving(true);
    try {
      if (config.modo === "editar") {
        const payload = soloFin
          ? { fin: periodo.fin, dia_completo: diaCompleto }
          : { inicio: periodo.inicio, fin: periodo.fin, dia_completo: diaCompleto, motivo };
        await updateMaterialReserva(reserva.id, payload);
        toast.success(soloFin ? "Devolución ampliada" : "Reserva actualizada");
      } else {
        await createMaterialReserva({
          item_ids: selIds,
          inicio: periodo.inicio,
          fin: periodo.fin,
          dia_completo: diaCompleto,
          motivo,
          recoger_ahora: config.modo === "ahora",
        });
        const n = selIds.length;
        toast.success(
          config.modo === "ahora"
            ? `${n === 1 ? seleccionados[0].nombre : `${n} equipos`} — ¡a grabar! Recuerda devolverlo a su sitio`
            : `${n === 1 ? "Equipo reservado" : `${n} equipos reservados`}`
        );
      }
      onOpenChange(false);
      onDone?.();
    } catch (e) {
      toast.error(getApiErrorMessage(e, "No se pudo guardar la reserva"));
    } finally {
      setSaving(false);
    }
  };

  if (!config) return null;

  const titulo =
    config.modo === "ahora" ? "Coger ahora" : config.modo === "editar" ? (soloFin ? "Ampliar devolución" : "Editar reserva") : "Reservar material";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {config.modo === "ahora" ? <Hand className="h-5 w-5" /> : <CalendarRange className="h-5 w-5" />}
            {titulo}
          </DialogTitle>
          <DialogDescription>
            {soloFin ? "¿Hasta cuándo lo vas a tener?" : "Elige los días en el calendario. Los días con un punto rojo ya tienen reservas."}
          </DialogDescription>
        </DialogHeader>

        <Separator className="-mx-6 w-auto" />

        {/* Equipos */}
        <div className="flex flex-col gap-2.5">
          <h3 className={SECCION}>{seleccionados.length === 1 ? "Equipo" : "Equipos"}</h3>
          <div className="flex flex-wrap gap-2">
            {seleccionados.map((it) => (
              <span key={it.id} className="inline-flex max-w-full min-w-0 items-center gap-2 rounded-full border bg-muted/50 py-1 pl-1 pr-3 text-sm">
                <MaterialFoto item={it} className="h-7 w-7 shrink-0 rounded-full" iconClassName="h-3.5 w-3.5" />
                <span className="min-w-0 truncate font-medium">{it.nombre}</span>
                {config.modo === "reservar" && seleccionados.length > 1 && (
                  <button type="button" onClick={() => toggleItem(it.id)} className="text-muted-foreground hover:text-foreground" aria-label={`Quitar ${it.nombre}`}>
                    ×
                  </button>
                )}
              </span>
            ))}
            {config.modo === "reservar" && otros.length > 0 && (
              <Button type="button" variant="ghost" size="sm" className="h-9 gap-1 rounded-full" onClick={() => setMostrarMas((v) => !v)}>
                <Plus className="h-4 w-4" /> Añadir equipos
              </Button>
            )}
          </div>
          {mostrarMas && (
            <div className="grid max-h-48 grid-cols-1 gap-1 overflow-y-auto rounded-lg border p-1 sm:grid-cols-2">
              {otros.map((it) => (
                <button
                  key={it.id}
                  type="button"
                  onClick={() => toggleItem(it.id)}
                  className="flex items-center gap-2 rounded-md px-2 py-1.5 text-left text-sm hover:bg-muted"
                >
                  <MaterialFoto item={it} className="h-8 w-8 shrink-0 rounded" iconClassName="h-4 w-4" />
                  <span className="min-w-0 flex-1 truncate">{it.nombre}</span>
                  <MaterialEstadoBadge estado={it.estado} className="shrink-0 scale-90" />
                </button>
              ))}
            </div>
          )}
        </div>

        <Separator className="-mx-6 w-auto" />

        <div className="grid min-w-0 gap-6 sm:grid-cols-[auto_1fr]">
          <div className="flex justify-center rounded-lg border">
            {soloFin ? (
              <Calendar
                mode="single"
                locale={es}
                weekStartsOn={1}
                selected={diaFin}
                onSelect={(d) => d && setDiaFin(d)}
                defaultMonth={diaFin}
                disabled={{ before: hoy }}
                modifiers={{ ocupado: diasOcupados }}
                modifiersClassNames={{ ocupado: OCUPADO_CLASS }}
              />
            ) : (
              <Calendar
                mode="range"
                locale={es}
                weekStartsOn={1}
                selected={rango}
                onSelect={(r) => setRango(r || undefined)}
                defaultMonth={rango?.from}
                disabled={{ before: hoy }}
                modifiers={{ ocupado: diasOcupados }}
                modifiersClassNames={{ ocupado: OCUPADO_CLASS }}
              />
            )}
          </div>

          <div className="flex min-w-0 flex-col gap-4">
            <h3 className={SECCION}>{soloFin ? "Devolución" : "Horario"}</h3>
            {soloFin && (
              <div className="flex flex-wrap gap-1.5">
                {[
                  ["Hoy", 0],
                  ["Mañana", 1],
                  ["En 3 días", 3],
                  ["En una semana", 7],
                ].map(([label, n]) => (
                  <Button key={label} type="button" size="sm" variant={diaFin && isSameDay(diaFin, addDays(hoy, n)) ? "secondary" : "outline"} className="h-9 sm:h-7 text-xs" onClick={() => setDiaFin(addDays(hoy, n))}>
                    {label}
                  </Button>
                ))}
              </div>
            )}

            <div className="flex rounded-lg border bg-muted/40 p-0.5">
              <button
                type="button"
                onClick={() => setDiaCompleto(true)}
                className={cn("flex flex-1 items-center justify-center gap-1.5 rounded-md px-3 py-1.5 text-sm font-medium", diaCompleto ? "bg-background shadow-sm" : "text-muted-foreground")}
              >
                <Sun className="h-4 w-4" /> Día completo
              </button>
              <button
                type="button"
                onClick={() => setDiaCompleto(false)}
                className={cn("flex flex-1 items-center justify-center gap-1.5 rounded-md px-3 py-1.5 text-sm font-medium", !diaCompleto ? "bg-background shadow-sm" : "text-muted-foreground")}
              >
                <Clock className="h-4 w-4" /> Con horas
              </button>
            </div>

            {!diaCompleto && (
              <div className="grid grid-cols-2 gap-3">
                {!soloFin && (
                  <div className="space-y-1">
                    <Label htmlFor="mat-hi" className="text-xs">Desde las</Label>
                    <Input id="mat-hi" type="time" step={900} value={horaInicio} onChange={(e) => setHoraInicio(e.target.value)} />
                  </div>
                )}
                <div className={cn("space-y-1", soloFin && "col-span-2")}>
                  <Label htmlFor="mat-hf" className="text-xs">{soloFin ? "Lo devuelvo a las" : "Hasta las"}</Label>
                  <Input id="mat-hf" type="time" step={900} value={horaFin} onChange={(e) => setHoraFin(e.target.value)} />
                </div>
              </div>
            )}
          </div>
        </div>

        {!soloFin && (
          <>
            <Separator className="-mx-6 w-auto" />
            <div className="space-y-1.5">
              <Label htmlFor="mat-motivo" className={SECCION}>Proyecto (opcional)</Label>
              <Input id="mat-motivo" value={motivo} onChange={(e) => setMotivo(e.target.value)} placeholder="Ej. Rodaje cliente X" maxLength={120} />
            </div>
          </>
        )}

        <Separator className="-mx-6 w-auto" />

        <div className="flex flex-col gap-3">
          <div className="rounded-lg bg-muted/50 px-3 py-2 text-sm">
            {errorPeriodo ? (
              <span className="text-muted-foreground">{errorPeriodo}</span>
            ) : (
              <span>
                <span className="font-medium">{seleccionados.length === 1 ? seleccionados[0].nombre : `${seleccionados.length} equipos`}</span>
                {" · "}
                {soloFin ? `desde ahora → ` : ""}
                {formatReservaRango({ ...periodo, dia_completo: diaCompleto, estado: "reservada", inicio: soloFin ? (reserva?.inicio ?? new Date().toISOString()) : periodo.inicio })}
              </span>
            )}
          </div>

          {conflictos.length > 0 && (
            <div className="flex gap-2 rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-900">
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
              <div className="flex flex-col gap-0.5">
                <span className="font-medium">Choca con otras reservas:</span>
                {conflictos.slice(0, 4).map((c) => (
                  <span key={c.id}>
                    {itemsById[c.item_id]?.nombre} — {usersByEmail[c.user_email]?.nombre || c.user_email} ({formatReservaRango(c)})
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>

        <DialogFooter className="-mx-6 border-t px-6 pt-4">
          <Button variant="outline" className="h-11 sm:h-9" onClick={() => onOpenChange(false)} disabled={saving}>
            Cancelar
          </Button>
          <Button onClick={submit} disabled={saving || !!errorPeriodo || !seleccionados.length || conflictos.length > 0} className="h-11 sm:h-9 gap-1.5">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
            {config.modo === "ahora" ? "Me lo llevo" : config.modo === "editar" ? "Guardar" : "Reservar"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

const SECCION = "text-xs font-semibold uppercase tracking-wide text-muted-foreground";

const OCUPADO_CLASS =
  "relative after:absolute after:bottom-0.5 after:left-1/2 after:h-1 after:w-1 after:-translate-x-1/2 after:rounded-full after:bg-red-500";

/** Mismo formato que guarda el backend, para comparar como string. */
function toUtc(iso) {
  return new Date(iso).toISOString().replace(/\.\d{3}Z$/, "+00:00");
}
